import * as React from 'react'
import { difference, flatMap, memoize } from 'lodash'

import type { CommonDeps } from '../base/CommonDeps'
import { ElementLabel } from './ElementLabel'
import { ExecutionElement } from './ExecutionElement'
import { renderCatch } from '../error/renderCatch'
import { SelectedOption } from '../ui/SelectedOption'
import { UnitImage } from '../unit/UnitImage'
import { UnitSelect } from '../unit/UnitSelect'
import { Warning } from '../ui/Warning'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import { type KnownUnit, unknownUnit, unitName } from '../unit/unit'
import { map } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { findById } from '../../../shared/lib/functional/identifiable.no-deps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import type { TimelineDetails } from './TimelineDetails'
import { BarcodeButton } from '../unit/BarcodeButton'
import { NoMatchesModal } from '../ui/NoMatchesModal'
import type { AppUnitType } from '../../../shared/domain/units/types.no-deps'
import { initialPromiseState, type PromiseState } from '../util/PromiseState'
import { cancelPromiseManagers, createPromiseManager } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import type { BarcodeResult } from '../unit/barcodeTypes'
import { assertExhaustion, type Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import i18next, { type TFunction } from 'i18next'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    allUnits: KnownUnit[]
    unitIds: number[]
    maxUnits: number
    answerValue: Flows.SelectedUnits
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.UnitSelectAnswer>
    renderCachedImage: RenderCachedImage
    unitTypes: number[]
    barcodeScanForUnits?: {
        scan: (units: KnownUnit[]) => Promise<BarcodeResult>
        fetchUnitType: (id: number) => Promise<AppUnitType>
    }
    disabled?: boolean
    dontFilterUnitsCheckbox?: React.ReactElement<unknown>
    openOptions?: () => void
}

interface State {
    hideWarning: boolean
    noMatches: boolean
    hasBarcodeLookup: PromiseState<boolean>
}

export class ElementUnitSelect extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = { hideWarning: false, noMatches: false, hasBarcodeLookup: initialPromiseState }

    public promiseManagers = {
        hasBarcodeLookup: createPromiseManager(() => this.props.deps, createStateLens(this, 'hasBarcodeLookup')),
    }

    public componentDidMount() {
        this.promiseManagers.hasBarcodeLookup.trigger(this.hasBarcodeLookup)
    }
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.allUnits !== this.props.allUnits) {
            this.promiseManagers.hasBarcodeLookup.trigger(this.hasBarcodeLookup)
        }
    }

    public componentWillUnmount() {
        cancelPromiseManagers(this.promiseManagers)
    }

    private hasBarcodeLookup = async () => {
        if (!this.props.barcodeScanForUnits) return false

        const unitTypes = await Promise.all(this.props.unitTypes.map(this.props.barcodeScanForUnits.fetchUnitType))
        const lookups = flatMap(unitTypes, (unitType) => unitType.options.lookup ?? [])
        return lookups.some((lookup) => lookup.type === 'barcode')
    }

    private isSingleSelect = () => this.props.maxUnits === 1
    private isMultiSelect = () => !this.isSingleSelect()

    private canSelect() {
        // If single select, can always select because it toggles
        if (this.isSingleSelect()) return true

        // If no limit, always can select
        if (this.props.maxUnits == null) return true

        return this.props.maxUnits > this.getSelectedUnitIds().length
    }

    private isRequiredIgnored() {
        return this.props.required && this.props.answerValue === 'no-units-available'
    }

    private getSelectedUnitIds = () => {
        const { answerValue } = this.props
        if (answerValue === 'no-units-available') return []
        return answerValue ?? []
    }

    private getFirstSelectedUnitId() {
        const selectedUnitIds = this.getSelectedUnitIds()
        return selectedUnitIds.length > 0 ? selectedUnitIds[0] : null
    }

    private getUnitWithFallback = (unitId: number) => this.getUnit(unitId) ?? unknownUnit(unitId)
    private getUnit = memoize((unitId: number) => findById(this.props.allUnits, unitId))

    private getUnits() {
        return this.props.unitIds.filter(this.filterUnitIds).map(this.getUnitWithFallback)
    }

    private getSelectedUnits = () => this.getSelectedUnitIds().map(this.getUnitWithFallback)

    private filterUnitIds = (unitId: number) => {
        const unit = this.getUnit(unitId)
        return this.props.unitTypes.includes(unit.type)
    }

    private getUnselectedUnits() {
        return pipe(
            difference(this.props.unitIds.filter(this.filterUnitIds), this.getSelectedUnitIds()),
            map(this.getUnitWithFallback)
        )
    }

    private getDropdownUnits() {
        return this.isMultiSelect() ? this.getUnselectedUnits() : this.getUnits()
    }

    private getDropdownUnitAncestors(): KnownUnit[] {
        return pipe(difference(this.props.unitIds, this.props.unitIds.filter(this.filterUnitIds)), map(this.getUnit))
    }

    private onChange(selection: Nullable<number[]>) {
        this.props.createAnswer((base) => ({ ...base, type: 'unit-select', value: selection }))
    }

    private onSelect = (unitId: Nullable<number>) => {
        if (unitId === null) {
            this.onChange([])
            return
        }
        const value = this.isSingleSelect() ? [unitId] : this.getSelectedUnitIds().concat(unitId)
        this.onChange(value)
    }

    private onRemove(unitId: number) {
        this.onChange(difference(this.getSelectedUnitIds(), [unitId]))
    }

    private handleBarcodeSelect = (result: BarcodeResult) => {
        switch (result.type) {
            case 'Success':
                return this.onSelect(result.unit.id)
            case 'NotFound':
            case 'Redirect':
                return this.setState({ noMatches: true })
            case 'Cancelled':
                // no-op
                return
            default:
                assertExhaustion(result)
        }
    }

    private handleTryAgain = async () => {
        this.setState({ noMatches: false })
        const units = this.getDropdownUnits()
        const selected = await this.props.barcodeScanForUnits.scan(units as KnownUnit[])
        this.handleBarcodeSelect(selected)
    }

    private handleCloseError = () => this.setState({ noMatches: false })

    private tryRender = () => {
        const props = this.props

        const units = this.getDropdownUnits()

        return (
            <ExecutionElement
                deps={props.deps}
                elementId={props.elementId}
                loopPathIds={props.loopPathIds}
                validationMessages={props.validationMessages}
                timelineDetails={props.timelineDetails}
            >
                <ElementLabel
                    required={props.required}
                    label={props.label}
                    hasBeenEdited={props.timelineDetails && props.timelineDetails.hasBeenEdited}
                    language={props.deps.language}
                    validationMessages={props.validationMessages}
                    openOptions={this.props.openOptions}
                />
                <div className="AppCore__flow__ElementUnitSelect">
                    {this.isMultiSelect() &&
                        this.getSelectedUnits().map((unit) => (
                            <SelectedOption
                                key={unit.id}
                                label={unitName(unit)}
                                leftNode={
                                    <UnitImage
                                        deps={this.props.deps}
                                        renderCachedImage={this.props.renderCachedImage}
                                        unit={unit}
                                    />
                                }
                                onRemove={() => this.onRemove(unit.id)}
                            />
                        ))}
                    {this.canSelect() && (
                        <UnitSelect
                            deps={props.deps}
                            onSelect={this.onSelect}
                            renderCachedImage={this.props.renderCachedImage}
                            selectedUnitId={this.isSingleSelect() ? this.getFirstSelectedUnitId() : null}
                            units={units}
                            ancestors={this.getDropdownUnitAncestors()}
                            disabled={props.disabled}
                        />
                    )}
                    {this.props.barcodeScanForUnits &&
                        this.promiseManagers.hasBarcodeLookup.render(
                            (hasBarcodeLookup) =>
                                hasBarcodeLookup && (
                                    <div className="AppCore__flow__ElementUnitSelect__barcode">
                                        <span className="AppCore__flow__ElementUnitSelect__search-label">
                                            {i18next.t('appcore:element.unitSelect.or')}
                                        </span>
                                        <BarcodeButton
                                            className="AppCore__flow__ElementUnitSelect__barcode-button"
                                            deps={props.deps}
                                            units={units}
                                            barcodeScanForUnits={props.barcodeScanForUnits.scan}
                                            onSelect={this.handleBarcodeSelect}
                                        >
                                            {i18next.t('appcore:element.unitSelect.barcode')}
                                        </BarcodeButton>
                                        {this.state.noMatches && (
                                            <NoMatchesModal
                                                i18n={i18next.t}
                                                actions={[
                                                    {
                                                        onClick: this.handleTryAgain,
                                                        label: i18next.t('appcore:element.unitSelect.tryAgain'),
                                                        color: 'red',
                                                    },
                                                    {
                                                        onClick: this.handleCloseError,
                                                        label: i18next.t('appcore:common.close'),
                                                        color: 'white',
                                                    },
                                                ]}
                                            />
                                        )}
                                    </div>
                                )
                        )}
                    {props.dontFilterUnitsCheckbox}
                    {this.isRequiredIgnored() && !this.state.hideWarning && (
                        <div className="AppCore__flow__ElementUnitSelect__warning">
                            <Warning
                                message={noAvailableUnitsMessage(i18next.t)}
                                onClear={() => this.setState({ hideWarning: true })}
                            />
                        </div>
                    )}
                </div>
            </ExecutionElement>
        )
    }
}

const noAvailableUnitsMessage = (i18n: TFunction) => i18n('appcore:element.unitSelect.noUnits')
